"use client"

import { CopyIcon, Eye, EyeOff, RefreshCw } from "lucide-react"
import { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux";
import { syncWallet } from "../redux/slices/walletSlice";
import moment from "moment";
import { useBlur } from '../context/BlurContext';
import { useAuth } from "../context/AuthContext";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import SendModal from "./SendModal";
import ReceiveModal from "./ReceiveModal";
import { ShieldModal } from "./shield";

function WalletDashboardCard() {

  const [sendModalOpen, setSendModalOpen] = useState(false)
  const [receiveModalOpen, setReceiveModalOpen] = useState(false)
  const [unshieldModalOpen, setUnshieldModalOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const { blurred } = useBlur();
  const mode = useSelector(state => state.mode.mode);
  const chains = useSelector(state => state.coins.chains);
  const { syncStatus, lastSyncAt } = useSelector(state => state.wallet);
  const [balances, setBalances] = useState({ USD: '$ 0.00', KAX: '$ 0.00' });
  const { user } = useAuth();
  // const [activeCurrency, setActiveCurrency] = useState("KAX")
  const [copied, setCopied] = useState(false);
  const [hideBalance, setHideBalance] = useState(false);


  // Remove the duplicate getCoins call - it's already handled by modeSlice.switchMode
  // and by WalletDashboard on initial mount
  useEffect(() => {
    // Just update loading state based on chains data availability
    if (chains.length > 0) {
      setIsLoading(false);
    }
  }, [chains]);

  // Get total balances in USD and KAX
  useEffect(() => {
    let totalUSD = 0;
    let kaxRate = 0;
    let kaxEquivalent = 0;

    if (!chains.loading) {
      chains.forEach(chain => {
        chain.coins.forEach(coin => {
          const coinBalance = parseFloat(coin.balance) || 0;
          const coinPrice = parseFloat(coin.price?.price) || 0;
          totalUSD += coinBalance * coinPrice;
          if (coin.symbol === 'KAX') {
            kaxRate = coinPrice;
          }
        });
      });
    }
    // Compute how many KAX equals the total USD value
    if (kaxRate > 0) {
      kaxEquivalent = totalUSD / kaxRate;
    }

    setBalances({
      USD: `$ ${totalUSD.toFixed(2)}`,
      // Show KAX as the amount of KAX equivalent to the USD total
      KAX: `${kaxEquivalent.toFixed(2)} KAX`
    });

  }, [chains]);

  const handleCopy = () => {
    const address = mode === 'private' ? user.wallet.railgunAddress : user.wallet.publicWalletAddress;
    navigator.clipboard.writeText(address);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <>
      <div id="tour-balance-card" className="account-card justify-between flex flex-col">
        <div className="account-header">

          {/* <div className="grid grid-cols-2 gap-5"> */}
          <div className="flex justify-between items-center gap-2 mb-4">
            <div className="flex items-center gap-2 text-gray-700">
              <div className="icon-holder me-1">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><g fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.2"><path d="M19 7V4a1 1 0 0 0-1-1H5a2 2 0 0 0 0 4h15a1 1 0 0 1 1 1v4h-3a2 2 0 0 0 0 4h3a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1" /><path d="M3 5v14a2 2 0 0 0 2 2h15a1 1 0 0 0 1-1v-4" /></g></svg>
              </div>
              <h5 className="font-semibold m-0">⁠Your total balance</h5>
            </div>
            {mode === 'private' && (
              <div className="flex items-center gap-2">
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip id="sync-tooltip">Sync Wallet {lastSyncAt ? `(Last: ${moment(lastSyncAt).format('HH:mm:ss')})` : ''}</Tooltip>}
                >
                  <button
                    className="btn-icon p-1 border"
                    onClick={() => dispatch(syncWallet(mode))}
                    disabled={syncStatus === 'syncing'}
                    id="tour-private-sync"
                  >
                    <RefreshCw className={`w-4 h-4 text-primary ${syncStatus === 'syncing' ? 'refresh-rotating' : ''}`} />
                  </button>
                </OverlayTrigger>
              </div>
            )}
          </div>
        </div>
        {/* Currency Tabs and Balance */}
        <div className="">


          {/* <div className="tabs-holder gap-0 mb-3 w-full">
            <span
              onClick={() => setActiveCurrency("KAX")}
              className={`${activeCurrency === "KAX" ? "active" : ''}`}
            >
              KAX
            </span>
            <span className="text-gray">/</span>
            <span
              onClick={() => setActiveCurrency("USD")}
              className={`${activeCurrency === "USD" ? "active" : ''}`}
            >
              USD
            </span>
          </div> */}

          <div className="flex flex-col gap-2">
            {isLoading ? (
              <div className="skeleton" style={{ width: '220px', height: '48px', borderRadius: '8px' }} />
            ) : (
              <div className="flex gap-3 items-start">
                {/* <img alt="KAX token icon" width={30} src="/assets/token-icon.svg" /> */}
                <div className="">
                  {/* KAX Balance */}
                  <div className={`lg:text-3xl text-4xl fw-bold flex items-center gap-2 secret ${blurred ? "blurred" : ""}`}>
                    {hideBalance ? "****" : balances.KAX}
                    {/* <span className="font-semibold text-14 ms-2">KAX</span> */}
                  </div>

                  {/* USD Balance */}
                  <div className={`lg:text-14 text-xl text-gray-700 secret ${blurred ? "blurred" : ""}`}>
                    {hideBalance ? "****" : balances.USD}
                    <span className="font-normal text-14 ms-2">USD</span>
                  </div>
                </div>
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip id="copy-tooltip">{hideBalance ? "Show balances from screen recording & screenshots" : "Hide balances from screen recording & screenshots"}</Tooltip>}
                >
                  <button className="p-2" onClick={() => setHideBalance(prev => !prev)}>{!hideBalance ? <><EyeOff size={18} /></> : <><Eye size={18} /></>}</button>
                </OverlayTrigger>
              </div>
            )}
          </div>


        </div>

      </div>
      <div id="tour-address-card" className="account-card justify-between flex flex-col">
        <div className="flex justify-between gap-2 items-center mb-4">
          <div className="flex items-center gap-2 text-gray-700">
            {/* <div className="icon-holder me-1">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><g fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.2"><circle cx="12" cy="12" r="10"/><path d="M12 2a14.5 14.5 0 0 0 0 20a14.5 14.5 0 0 0 0-20M2 12h20"/></g></svg>
            </div> */}
            <h5 className="font-semibold m-0">{mode === 'private' ? "Your Private Address" : "Your Public Address"}</h5>
          </div>
        </div>
        <div className="input-group flex-nowrap">
          <p className="" style={{ wordBreak: "break-all" }}>{mode === 'private' ? user.wallet.railgunAddress : user.wallet.publicWalletAddress}</p>
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip id="copy-tooltip">{copied ? "Copied!" : "Copy wallet address"}</Tooltip>}
          >
            <button className="" onClick={handleCopy}><CopyIcon size={14} /></button>
          </OverlayTrigger>
        </div>
      </div>

      {sendModalOpen && <SendModal onClose={() => setSendModalOpen(false)} />}
      {receiveModalOpen && <ReceiveModal onClose={() => setReceiveModalOpen(false)} />}
      {unshieldModalOpen && <ShieldModal onClose={() => setUnshieldModalOpen(false)} selectedToken={null} mode="private" />}
    </>
  )
}

export default WalletDashboardCard;
